import { Outlet, NavLink } from 'react-router-dom'
import { LayoutDashboard, Car, User, LogOut, Menu, Star, MessageCircle, CreditCard, Users, Award, History, LifeBuoy, Bell, TrendingUp, Wallet, Package, Crown } from 'lucide-react'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/hooks/useAuth'
import { WelcomeTour } from '@/components/shared/WelcomeTour'
import { AnnouncementBanner } from '@/components/shared/AnnouncementBanner'
import { MaintenanceBanner } from '@/components/shared/MaintenanceBanner'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { getInitials, cn } from '@/lib/utils'
import { NotificationsDropdown } from '@/components/shared/NotificationsDropdown'
import { useTotalUnread } from '@/hooks/useMessages'
import { BrandLogo } from '@/components/shared/BrandLogo'

const navItems = [
  { to: '/aluno/dashboard',      label: 'Dashboard',          icon: LayoutDashboard },
  { to: '/aluno/instrutores',    label: 'Buscar instrutores',  icon: Car },
  { to: '/aluno/mensagens',      label: 'Mensagens',           icon: MessageCircle, badgeKey: 'mensagens' },
  { to: '/aluno/certificados',   label: 'Certificados',        icon: Award },
  { to: '/aluno/carteira',       label: 'Minha carteira',      icon: Wallet },
  { to: '/aluno/pacotes',        label: 'Pacotes',             icon: Package },
  { to: '/aluno/premium',        label: 'Premium',             icon: Crown },
  { to: '/aluno/pagamentos',     label: 'Pagamentos',          icon: CreditCard },
  { to: '/aluno/avaliacoes',     label: 'Minhas avaliações',   icon: Star },
  { to: '/aluno/historico',       label: 'Histórico',            icon: History },
  { to: '/aluno/indicacoes',     label: 'Indicações',          icon: Users },
  { to: '/aluno/progresso',      label: 'Progresso',           icon: TrendingUp },
  { to: '/aluno/notificacoes',   label: 'Notificações',        icon: Bell },
  { to: '/aluno/suporte',        label: 'Suporte',             icon: LifeBuoy },
  { to: '/aluno/perfil',         label: 'Meu perfil',          icon: User },
] as const

function Sidebar({ onClose }: { onClose?: () => void }) {
  const { profile, signOut } = useAuth()
  const totalUnread = useTotalUnread()

  return (
    <div className="flex h-full flex-col bg-gradient-to-b from-brand-700 via-brand-800 to-brand-900">

      {/* Logo */}
      <div className="flex h-16 shrink-0 items-center px-5 border-b border-white/10">
        <BrandLogo onDark className="h-8" />
      </div>

      {/* User card */}
      <div className="mx-3 mt-4 rounded-xl bg-white/10 ring-1 ring-white/10 px-3 py-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9 ring-2 ring-white/30 shrink-0">
            <AvatarImage src={profile?.avatar_url ?? undefined} />
            <AvatarFallback className="bg-white/20 text-white text-sm font-semibold">
              {getInitials(profile?.full_name ?? 'A')}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white truncate leading-tight">
              {profile?.full_name ?? 'Aluno'}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
              <p className="text-[11px] text-white/50">Aluno</p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 pt-5 pb-2 space-y-1">
        <p className="px-3 mb-2 text-[10px] font-semibold tracking-widest uppercase text-white/30">
          Menu
        </p>
        {navItems.map(({ to, label, icon: Icon, ...rest }) => {
          const hasBadge = 'badgeKey' in rest && rest.badgeKey === 'mensagens'
          const badgeCount = hasBadge ? totalUnread : 0
          return (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150',
                  isActive
                    ? 'bg-white text-brand-700 shadow-lg shadow-black/10'
                    : 'text-white/65 hover:bg-white/10 hover:text-white',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={cn(
                      'h-4 w-4 shrink-0 transition-colors',
                      isActive ? 'text-brand-600' : 'text-white/50 group-hover:text-white',
                    )}
                  />
                  <span className="flex-1">{label}</span>
                  {badgeCount > 0 && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          )
        })}
      </nav>

      {/* Sign out */}
      <div className="px-3 pb-5 border-t border-white/10 pt-3 space-y-1">
        <button
          onClick={signOut}
          className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/40 hover:bg-white/10 hover:text-white/80 transition-all duration-150"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          Sair da conta
        </button>
      </div>
    </div>
  )
}

export default function StudentLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-gray-950">

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 lg:left-0 lg:z-50">
        <Sidebar />
      </aside>

      {/* Mobile sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-y-0 left-0 z-50 w-64 lg:hidden"
            >
              <Sidebar onClose={() => setSidebarOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">

        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-slate-200 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm px-4 lg:px-6">
          <button
            className="lg:hidden -ml-1 flex h-9 w-9 items-center justify-center rounded-xl hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
            onClick={() => setSidebarOpen(true)}
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5 text-slate-600 dark:text-gray-400" />
          </button>

          {/* Mobile logo */}
          <BrandLogo className="h-7 lg:hidden" />

          <div className="flex-1" />
          <NotificationsDropdown />
        </header>

        <MaintenanceBanner />
        <AnnouncementBanner />
        <main className="min-w-0 flex-1 p-4 lg:p-6">
          <Outlet />
          <WelcomeTour role="student" />
        </main>
      </div>
    </div>
  )
}
